import { memo, useMemo, useState } from "react";
import { ChevronDown, Gauge, ShieldAlert, KeyRound, Users, Server, Landmark, FileCode2 } from "lucide-react";
import { computeFootprint, type FootprintCategory, type FootprintCategoryId } from "@/lib/intel/footprint";

const ICON: Record<FootprintCategoryId, typeof KeyRound> = {
  breach: KeyRound,
  social: Users,
  infrastructure: Server,
  financial: Landmark,
  metadata: FileCode2,
};

function toneFor(exposure: number) {
  if (exposure >= 65) return { text: "text-danger", bar: "bg-danger", ring: "border-danger/40 bg-danger/10" };
  if (exposure >= 45) return { text: "text-[oklch(0.82_0.20_50)]", bar: "bg-[oklch(0.72_0.20_50)]", ring: "border-[oklch(0.72_0.20_50)]/40 bg-[oklch(0.72_0.20_50)]/10" };
  if (exposure >= 25) return { text: "text-amber", bar: "bg-amber", ring: "border-amber/40 bg-amber/10" };
  return { text: "text-success", bar: "bg-success", ring: "border-success/40 bg-success/10" };
}

function CategoryRow({ c }: { c: FootprintCategory }) {
  const [open, setOpen] = useState(false);
  const tone = toneFor(c.exposure);
  const Icon = ICON[c.id];
  return (
    <li className="rounded border border-panel-border bg-background/40">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-start gap-3 p-2.5 text-left hover:bg-panel/40">
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone.text}`} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium">{c.label}</span>
            <span className={`rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${tone.ring} ${tone.text}`}>
              {c.unmeasured ? "no data" : `${c.grade} · ${c.exposure}/100`}
            </span>
          </div>
          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-panel">
            <div className={`h-full rounded-full ${tone.bar}`} style={{ width: `${c.unmeasured ? 0 : c.exposure}%` }} />
          </div>
          <div className="mt-1.5 text-xs text-muted-foreground">{c.blurb}</div>
        </div>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-2 border-t border-panel-border p-2.5">
          {c.signals.length === 0 ? (
            <div className="text-xs text-muted-foreground">
              {c.unmeasured
                ? "No collector in this scan covered this category — the score excludes it rather than assuming zero exposure."
                : "Collectors ran for this category and returned no exposure signals."}
            </div>
          ) : (
            c.signals.map((s, i) => (
              <div key={`${s.source}-${i}`} className="rounded border border-panel-border/60 bg-panel/30 p-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-cyan">{s.label}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">+{s.weight}</span>
                </div>
                <div className="mt-1 text-xs">{s.detail}</div>
                <div className="mt-1 font-mono text-[10px] text-muted-foreground">{s.source}</div>
              </div>
            ))
          )}
          <div className="text-xs text-muted-foreground italic">{c.advice}</div>
        </div>
      )}
    </li>
  );
}

function FootprintScorePanelImpl({
  collectors,
  findings,
  compact,
}: {
  collectors: Array<{ source: string; status?: string; summary?: string; data?: unknown }> | undefined;
  findings?: Array<{ severity?: string; title?: string }>;
  compact?: boolean;
}) {
  const fp = useMemo(() => computeFootprint(collectors, findings), [collectors, findings]);
  const [open, setOpen] = useState(!compact);
  const tone = toneFor(fp.exposure);
  const pct = Math.max(0, Math.min(1, (fp.score - 300) / 550));

  return (
    <section className="glass rounded-xl p-4">
      <div className="flex items-center gap-2">
        <Gauge className="h-3.5 w-3.5 text-cyan" />
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">
          Digital Footprint Score · exposure rating
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-4">
        <div>
          <div className={`font-display text-4xl font-semibold leading-none ${tone.text}`}>{fp.score}</div>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">300 – 850 · higher is safer</div>
        </div>
        <div className="w-full flex-1 sm:w-auto sm:min-w-[180px]">
          <div className="h-2 w-full overflow-hidden rounded-full bg-gradient-to-r from-danger via-amber to-success">
            <div className="h-full" style={{ marginLeft: `${pct * 100}%`, width: 2, background: "white" }} />
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <span className={`rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${tone.ring} ${tone.text}`}>
              {fp.band} exposure · grade {fp.grade}
            </span>
            <span className="font-mono text-[10px] text-muted-foreground">{fp.exposure}/100 exposed · {fp.coverage}% category coverage</span>
          </div>
        </div>
      </div>

      <div className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
        <ShieldAlert className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${tone.text}`} />
        <span>{fp.headline}</span>
      </div>

      {compact && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-2 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-cyan hover:underline"
        >
          {open ? "Hide breakdown" : "Show category breakdown"}
          <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      )}

      {open && <ul className="mt-3 space-y-2">{fp.categories.map((c) => <CategoryRow key={c.id} c={c} />)}</ul>}
    </section>
  );
}

/** Memoised: these panels render large evidence sets and must not re-render on every keystroke. */
export const FootprintScorePanel = memo(FootprintScorePanelImpl);
